import { zodResolver } from '@hookform/resolvers/zod';
import { Stack } from '@mui/material';
import { useMemo } from 'react';
import type { FieldError } from 'react-hook-form';
import { z } from 'zod';
import { useForm } from '../../../../hooks/use-form';
import { ControlledCheckbox } from '../../../../inputs/forms/controlled-checkbox/controlled-checkbox';
import { ControlledTextField } from '../../../../inputs/forms/controlled-text-field/controlled-text-field';
import { Button } from '../../../../primitives/button';
import { RevealedFieldError } from '../revealed-field-error/revealed-field-error';
import { TWO_STEP_FIELD_REFUSALS, type TwoStepError } from './two-step.types';

const sixDigitCode = z
  .string()
  .transform((code) => code.replace(/\s/g, ''))
  .pipe(z.string().regex(/^\d{6}$/, 'Enter the 6-digit code.'));

const supportCode = z
  .string()
  .transform((code) => code.toUpperCase().replace(/[^A-Z2-7]/g, ''))
  .pipe(z.string().regex(/^[A-Z2-7]{8}$/, 'Enter the code from iDU support, like ABCD-EFGH.'));

const codeSchema = z.object({ code: sixDigitCode, rememberDevice: z.boolean() });
const supportCodeSchema = z.object({ code: supportCode, rememberDevice: z.boolean() });
const phoneSchema = z.object({
  phoneNumber: z
    .string()
    .trim()
    .regex(/^(?=.{8,20}$)\+?[\d\s()-]+$/, 'Enter a mobile number.'),
});

type CodeFormInput = z.input<typeof codeSchema>;
type CodeFormOutput = z.output<typeof codeSchema>;
type PhoneFormData = z.infer<typeof phoneSchema>;

const serverError = (refusal: string, error: TwoStepError | null): FieldError | undefined =>
  error?.code === refusal ? { type: 'server', message: error.message } : undefined;

type CodeFormProps = {
  submitLabel: string;
  isSupportCode?: boolean;
  rememberDays?: number;
  busy: boolean;
  error: TwoStepError | null;
  onSubmit: (code: string, rememberDevice: boolean) => void;
};

export const TwoStepCodeForm = ({
  submitLabel,
  isSupportCode = false,
  rememberDays,
  busy,
  error,
  onSubmit,
}: CodeFormProps) => {
  const errors = useMemo(() => {
    const code = serverError(TWO_STEP_FIELD_REFUSALS.code, error);
    return code && { code };
  }, [error]);
  const { control, handleSubmit } = useForm<CodeFormInput, unknown, CodeFormOutput>({
    resolver: zodResolver(isSupportCode ? supportCodeSchema : codeSchema),
    defaultValues: { code: '', rememberDevice: false },
    errors,
    unsavedChangesGuard: false,
  });

  return (
    <Stack
      component="form"
      noValidate
      sx={{ width: '100%', gap: 1 }}
      onSubmit={handleSubmit(({ code, rememberDevice }) => onSubmit(code, rememberDevice))}
    >
      <ControlledTextField
        name="code"
        control={control}
        label={isSupportCode ? 'Code from iDU support' : '6-digit code'}
        className="MuiFormControl-noMargin"
        slots={{ formHelperText: RevealedFieldError }}
        autoComplete="one-time-code"
        autoFocus
        fullWidth
        disabled={busy}
        slotProps={{
          htmlInput: isSupportCode ? { autoCapitalize: 'characters' } : { inputMode: 'numeric' },
        }}
      />
      {rememberDays !== undefined && (
        <ControlledCheckbox
          name="rememberDevice"
          control={control}
          disabled={busy}
          inline
          label={`Don't ask again on this device for ${rememberDays} days`}
          sx={{ p: 1.25 }}
        />
      )}
      <Button type="submit" size="large" fullWidth loading={busy} loadingPosition="start">
        {submitLabel}
      </Button>
    </Stack>
  );
};

type PhoneFormProps = {
  defaultPhoneNumber?: string;
  busy: boolean;
  error: TwoStepError | null;
  onSubmit: (phoneNumber: string) => void;
};

export const TwoStepPhoneForm = ({ defaultPhoneNumber, busy, error, onSubmit }: PhoneFormProps) => {
  const errors = useMemo(() => {
    const phoneNumber =
      serverError(TWO_STEP_FIELD_REFUSALS.phoneNumber, error) ??
      serverError(TWO_STEP_FIELD_REFUSALS.phoneCountry, error);
    return phoneNumber && { phoneNumber };
  }, [error]);
  const { control, handleSubmit } = useForm<PhoneFormData>({
    resolver: zodResolver(phoneSchema),
    defaultValues: { phoneNumber: defaultPhoneNumber ?? '' },
    errors,
    unsavedChangesGuard: false,
  });

  return (
    <Stack
      component="form"
      noValidate
      sx={{ width: '100%', gap: 1 }}
      onSubmit={handleSubmit(({ phoneNumber }) => onSubmit(phoneNumber))}
    >
      <ControlledTextField
        name="phoneNumber"
        control={control}
        label="Mobile number"
        className="MuiFormControl-noMargin"
        slots={{ formHelperText: RevealedFieldError }}
        type="tel"
        autoComplete="tel"
        autoFocus
        fullWidth
        disabled={busy}
      />
      <Button type="submit" size="large" fullWidth loading={busy} loadingPosition="start">
        Text Me a Code
      </Button>
    </Stack>
  );
};
